/**
 * ModuleTrainsGuidePage.tsx — Curated module trains: pick a bundle that covers
 *                              the full diffuser→plume→bed→O₂→algae→compliance
 *                              pipeline for a given reservoir type.
 *
 * One-click enable activates every module in the train. Each train links to
 * its dedicated summary page with critical results and recommended improvements.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
  TrainTrack, ArrowLeft, ArrowRight, Check, CheckCircle2,
  Workflow, BookOpen, ChevronRight,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { MODULE_TRAINS, type ModuleTrain } from '@/lib/moduleTrains';
import { MODULES, useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleInfoModal from '@/components/ModuleInfoModal';

export default function ModuleTrainsGuidePage() {
  const navigate = useNavigate();
  const { isEnabled, toggle, enabledList } = useModulesEnabled();

  // enabledList is an OptModule[] (not a function) — map to a Set of ids
  const enabledSet = useMemo(() => new Set(enabledList.map(m => m.id)), [enabledList]);

  /** % of train's modules currently enabled. */
  const trainProgress = (t: ModuleTrain) => {
    if (t.moduleIds.length === 0) return 0;
    const on = t.moduleIds.filter(id => enabledSet.has(id)).length;
    return Math.round((on / t.moduleIds.length) * 100);
  };

  const enableTrain = (t: ModuleTrain) => {
    // Enable every module in the train that isn't already on.
    for (const id of t.moduleIds) {
      if (!isEnabled(id)) toggle(id);
    }
  };

  const disableTrain = (t: ModuleTrain) => {
    for (const id of t.moduleIds) {
      if (isEnabled(id)) toggle(id);
    }
  };

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={TrainTrack} title="Module Trains — Curated Bundles"
        subtitle="Pick a coherent module bundle covering the full pipeline from diffuser through to compliance"
        actions={
          <button onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        }
      />

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <strong className="text-slate-200">How trains work:</strong> Each train is a curated bundle of modules ' +
        chosen to cover one reservoir type completely — from the bubble physics at the diffuser, through plume rise,
        bed exchanges, hypolimnion O₂, algae and finally regulatory compliance. Trains are <em>conflict-free</em>
        by construction: any soft conflicts inside a train are documented and physically justified. Click <em>Enable train</em>
        to flip every module in the bundle, then <em>View summary</em> for the integrated critical-results dashboard.
      </div>

      <div className="grid grid-cols-1 gap-4">
        {MODULE_TRAINS.map(t => {
          const progress = trainProgress(t);
          const fullyOn = progress === 100;
          return (
            <section key={t.id} className={`rounded-2xl border-2 p-5 ${fullyOn ? 'border-emerald-700 bg-emerald-900/10' : 'border-slate-700 bg-slate-900'}`}>
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-lg font-bold text-slate-100">{t.label}</h2>
                    {fullyOn && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-900/40 text-emerald-200 text-xs font-semibold px-2 py-0.5"><CheckCircle2 className="w-3 h-3" /> ACTIVE</span>}
                  </div>
                  <div className="text-sm text-sky-300 mt-1">{t.shortDesc}</div>
                  <p className="text-xs text-slate-400 mt-2 leading-relaxed">{t.longDesc}</p>
                  <div className="text-xs text-slate-500 mt-2">
                    <strong className="text-slate-300">Best for:</strong> {t.bestFor}
                  </div>
                </div>
                <div className="flex flex-col gap-2 md:min-w-[200px]">
                  <div className="text-xs text-slate-400">Progress:</div>
                  <div className="w-full h-2 rounded-full bg-slate-800 overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${progress}%` }} />
                  </div>
                  <div className="text-xs text-slate-300 text-right">{progress}% of {t.moduleIds.length} modules</div>
                  <button onClick={() => enableTrain(t)} disabled={fullyOn}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-slate-500 px-3 py-1.5 text-xs font-semibold text-white">
                    <Check className="w-3.5 h-3.5" /> {fullyOn ? 'All enabled' : 'Enable train'}
                  </button>
                  {progress > 0 && (
                    <button onClick={() => disableTrain(t)}
                      className="rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-700">
                      Disable train
                    </button>
                  )}
                  <button onClick={() => navigate(`/train-summary/${t.id}`)}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-sky-600 hover:bg-sky-500 px-3 py-1.5 text-xs font-semibold text-white">
                    View summary <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Stage pipeline */}
              <div className="mt-4 overflow-x-auto">
                <div className="flex items-stretch gap-1 min-w-max">
                  {t.stages.map((s, i) => (
                    <div key={i} className="flex items-stretch">
                      <div className="rounded-lg border border-slate-700 bg-slate-950/50 p-3 min-w-[180px]">
                        <div className="text-[10px] uppercase tracking-wider text-sky-300 font-semibold">Stage {i + 1}</div>
                        <div className="text-sm font-bold text-slate-100">{s.stage}</div>
                        <div className="text-[11px] text-slate-400 mt-1 leading-snug">{s.delivers}</div>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {s.moduleIds.length === 0 ? (
                            <span className="text-[10px] italic text-slate-600">(no modules at this stage)</span>
                          ) : s.moduleIds.map(id => {
                            const mod = MODULES.find(m => m.id === id);
                            const on = enabledSet.has(id);
                            return (
                              <span key={id} className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] border ${on ? 'border-emerald-700 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-900 text-slate-400'}`} title={mod?.label}>
                                {on && <Check className="w-2.5 h-2.5" />}
                                {mod?.label || id}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                      {i < t.stages.length - 1 && (
                        <div className="flex items-center text-slate-600 px-1">
                          <ChevronRight className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Soft-conflict explainer */}
              {t.acceptedSoftConflicts.length > 0 && (
                <div className="mt-3 rounded-lg border border-amber-700/50 bg-amber-900/10 p-2.5 text-[11px] text-amber-200/90">
                  <div className="font-semibold mb-1 flex items-center gap-1"><BookOpen className="w-3 h-3" /> Accepted soft conflicts in this train:</div>
                  <ul className="list-disc ml-4 space-y-0.5">
                    {t.acceptedSoftConflicts.map((c, i) => (
                      <li key={i}><code className="text-amber-300">{c.a}</code> ↔ <code className="text-amber-300">{c.b}</code>: {c.reason}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Modules in train — quick links */}
              <div className="mt-3 flex flex-wrap gap-1.5 items-center">
                <span className="text-[11px] text-slate-500">Modules ({t.moduleIds.length}):</span>
                {t.moduleIds.map(id => (
                  <span key={id}>
                    <ModuleInfoModal moduleId={id} triggerLabel={MODULES.find(m => m.id === id)?.label || id} />
                  </span>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2"><Workflow className="w-4 h-4 text-rb-brand" /> Selecting a train</div>
        <ol className="list-decimal ml-5 space-y-1">
          <li>Identify your reservoir's dominant operational issue: bloom + T&O risk, deep-stratification Mn risk, internal P loading, or algorithmic placement.</li>
          <li>Pick the corresponding train above. The pipeline diagram shows which physics module is active at each pipeline stage.</li>
          <li>Click <em>Enable train</em> to activate every module at once.</li>
          <li>Click <em>View summary</em> for the integrated critical-results dashboard and required-improvements list.</li>
          <li>Trains can be combined: enabling Train A then Train D will overlay both. The conflict checker will flag any incompatibilities.</li>
        </ol>
      </div>
    </div>
  );
}
